import { useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import {
  SCHEDULE_FREQUENCY,
  DAYS_TO_FREQUENCY,
} from 'api/types/card-schedule.types';
import {
  useGetCardScheduleQuery,
  useGetCardScheduleByAnswerIdQuery,
  useCreateCardScheduleMutation,
  useUpdateCardScheduleMutation,
  useDeleteCardScheduleMutation,
} from 'store/api/card-schedules/card-schedules.api';

import type {
  ScheduleFrequency,
  CreateCardSchedulePayload,
} from 'api/types/card-schedule.types';
import type { Dispatch } from 'store/store.types';

// Hook for querying by card ID (card menu, schedule modal)
export function useCardSchedule(cardId: number | undefined) {
  const dispatch = useDispatch<Dispatch>();

  const {
    data: schedule,
    isLoading,
    error,
  } = useGetCardScheduleQuery(cardId!, { skip: !cardId });

  const [createSchedule, { isLoading: isCreating }] =
    useCreateCardScheduleMutation();
  const [updateSchedule, { isLoading: isUpdating }] =
    useUpdateCardScheduleMutation();
  const [deleteScheduleMutation, { isLoading: isDeleting }] =
    useDeleteCardScheduleMutation();

  const hasSchedule = useMemo(() => !!schedule, [schedule]);

  // Returns 'unscheduled' if no schedule OR if schedule exists but frequencyDays is null (paused)
  const frequency: ScheduleFrequency = useMemo(
    () =>
      (schedule?.frequencyDays != null
        ? DAYS_TO_FREQUENCY[schedule.frequencyDays]
        : undefined) ?? SCHEDULE_FREQUENCY.UNSCHEDULED,
    [schedule],
  );

  // True if schedule exists and has active frequency days
  const isScheduled = useMemo(() => !!schedule?.frequencyDays, [schedule]);

  const saveSchedule = useCallback(
    async (payload: CreateCardSchedulePayload) => {
      if (!cardId) return;

      let result;
      if (hasSchedule) {
        result = await updateSchedule({ cardId, payload }).unwrap();
      } else {
        result = await createSchedule({ cardId, payload }).unwrap();
      }
      // Keep Redux card store in sync
      dispatch.cards.updateCardSchedule({ cardId, schedule: result });
    },
    [cardId, hasSchedule, createSchedule, updateSchedule, dispatch],
  );

  const deleteSchedule = useCallback(async () => {
    if (!cardId) return;
    await deleteScheduleMutation(cardId).unwrap();
    dispatch.cards.updateCardSchedule({ cardId, schedule: null });
  }, [cardId, deleteScheduleMutation, dispatch]);

  return {
    schedule,
    hasSchedule, // True if schedule record exists (may be paused)
    isScheduled, // True if schedule exists AND has active frequency
    frequency,
    isLoading,
    isSaving: isCreating || isUpdating || isDeleting,
    error,
    saveSchedule,
    deleteSchedule,
  };
}

// Hook for querying by answer ID (ask-klue thread view)
export function useCardScheduleByAnswerId(answerId: string | undefined) {
  const {
    data: schedule,
    isLoading,
    error,
  } = useGetCardScheduleByAnswerIdQuery(answerId!, { skip: !answerId });

  return {
    schedule,
    hasSchedule: !!schedule,
    linkedCardId: schedule?.cardId,
    isLoading,
    error,
  };
}
